/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1903853583 — Знижки */

import React, { useEffect, useState } from 'react';
import { getCurrencyInSubUnits } from '../../../shared';
import { useAppStore } from '../../../shared/hooks/useAppStore';
import { useDebounce } from '../../../shared/hooks/useDebounce';
import { VolumeLimits } from '../../../shared/types';
import { AmountSubmittedScreen, EVENT_NAMES, useSendEventLog } from '../../event-log';
import { VOLUME_INPUT_DEBOUNCE_MS } from '../payment.constants';
import { calculateDiscountedAmount } from '../payment.utils';
import { useAmountValidation } from './useAmountValidation';
import { useOrderAmount } from './useOrderAmount';

interface UseVolumeOptionsOptions {
    /**
     * When true and `vm.planogram` exists, planogram item prices are used as
     * the available volume chips (CarWash semantics where volume ≈ amount).
     * When false, `defaultVolumes` is always used (Water/Pulse semantics).
     */
    usePlanogramVolumes?: boolean;
    /** Key used to resolve the `amount submitted - {screenName}` event-log name. */
    screenName?: AmountSubmittedScreen;
}

export function useVolumeOptions(
    limits: VolumeLimits,
    defaultVolumes: readonly number[],
    exactPricePerUnit?: number,
    { usePlanogramVolumes = false, screenName }: UseVolumeOptionsOptions = {},
) {
    const vm = useAppStore((s) => s.vm);
    const order = useAppStore((s) => s.order);
    const { sendEvent } = useSendEventLog();

    const [amount, setAmount] = useState<number>(0);
    const [originalAmount, setOriginalAmount] = useState<number>(0);
    const { isAmountSent, submitAmount } = useOrderAmount();

    // min = 0 — the lower bound check belongs to the volume side (limits.MIN).
    // We only need the money-ceiling validation against vm.max_credit_app.
    const { validate } = useAmountValidation({ min: 0 });

    const [volume, setVolume] = useState<number | ''>('');
    const [customVolume, setCustomVolume] = useState<number | ''>();
    // The predefined chip the user explicitly tapped. Reset when a custom
    // amount is typed so loyalty stays tied to a real option, not a typed value.
    const [selectedOption, setSelectedOption] = useState<number | null>(null);

    const debouncedVolume = useDebounce(volume, VOLUME_INPUT_DEBOUNCE_MS);
    const planogramBasedPricePerUnit = vm.planogram ? vm.planogram[0].price : 0;
    const pricePerUnit = exactPricePerUnit || planogramBasedPricePerUnit;
    // Chip labels show the planogram pre-discount price — the discount UI
    // on PriceDisplay communicates the actual saving.
    const planogramVolumes = usePlanogramVolumes
        ? vm.planogram?.map((planogramItem) => planogramItem.price)
        : undefined;

    const amountInCurrency = volume === '' ? 0 : Number(volume) * pricePerUnit;
    const moneyError = volume === '' ? null : validate(amountInCurrency);
    const isWithinVolumeMin = volume !== '' && volume >= limits.MIN;
    const volumeError: string | null = isWithinVolumeMin ? moneyError : null;
    const isPaymentButtonVisible = isWithinVolumeMin && moneyError === null;

    const resolvedVolumes = planogramVolumes || defaultVolumes;
    // Cheapest predefined option — the only one redeemable with loyalty points (SWRN-1739).
    const cheapestVolume = resolvedVolumes.length
        ? Math.min(...resolvedVolumes)
        : null;
    // Loyalty is offered only when the cheapest chip is explicitly selected —
    // never for another option or a typed custom amount (SWRN-1739).
    const isCheapestSelected =
        cheapestVolume !== null && selectedOption === cheapestVolume;

    function onCustomVolumeChange(event: React.ChangeEvent<HTMLInputElement>) {
        setCustomVolume(Number(event.target.value));
        setVolume(Number(event.target.value));
        setSelectedOption(null);
    }

    function selectVolumeOption(value: number) {
        setVolume(value);
        setCustomVolume(value);
        setSelectedOption(value);
    }

    useEffect(() => {
        const preDiscountCurrency = Number(debouncedVolume) * pricePerUnit;
        const preDiscountSubunits = getCurrencyInSubUnits(preDiscountCurrency);
        const discountedSubunits = calculateDiscountedAmount(
            preDiscountCurrency,
            vm?.campaign?.discount_percentage,
        );
        setOriginalAmount(preDiscountSubunits);
        setAmount(discountedSubunits);
        submitAmount(discountedSubunits);
        if (screenName && Number(debouncedVolume) > 0) {
            sendEvent({ name: EVENT_NAMES.amountSubmitted[screenName] });
        }
    }, [debouncedVolume]);

    return {
        vm,
        order,
        volume,
        customVolume,
        amount,
        originalAmount,
        isAmountSent,
        isPaymentButtonVisible,
        volumeError,
        availableVolumes: resolvedVolumes,
        cheapestVolume,
        isCheapestSelected,
        selectVolumeOption,
        onCustomVolumeChange,
    };
}
